import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AddMaintenanceModal from './AddMaintenanceModal';
import { formatDate } from '../../../utils/formatters';
import { usePreferences } from '../../../hooks/usePreferences';
import MaintenanceAdvisor from '../../../components/MaintenanceAdvisor';
import { supabase } from '../../../lib/supabaseClient';

/**
 * Maintenance History Tab Component
 * Displays a timeline of maintenance activities for a specific asset.
 * 
 * @param {Object} props
 * @param {string} props.assetTag - The tag of the asset
 * @param {Array} props.maintenanceHistory - List of maintenance records from DB
 * @param {Function} props.onSuccess - Callback to refresh data after adding a record
 */
const MaintenanceTab = ({ assetTag, maintenanceHistory, onSuccess }) => {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this maintenance record?')) return;
    try {
      const { error } = await supabase.from('maintenance').delete().eq('id', id);
      if (error) throw error;
      onSuccess?.();
    } catch (err) {
      console.error('Error deleting maintenance:', err);
      alert(err.message || 'Failed to delete maintenance record.');
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const { preferences } = usePreferences();
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

  const getMaintenanceTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'repair':
        return 'bg-error/10 text-error border-error/20';
      case 'routine service':
        return 'bg-success/10 text-success border-success/20';
      case 'hardware upgrade':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'software patch':
        return 'bg-info/10 text-info border-info/20';
      default:
        return 'bg-accent/10 text-accent border-accent/20';
    }
  };

  const totalMaintenanceCost = maintenanceHistory?.reduce((sum, record) => sum + (parseFloat(record?.cost) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header with Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">{t('asset_details.maintenance_history_tab')}</h3>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Icon name="ClipboardList" size={14} />
              {t('asset_details.total_records', { count: maintenanceHistory?.length || 0 })}
            </span>
            <span className="flex items-center gap-1.5">
              <Icon name="DollarSign" size={14} />
              {t('asset_details.total_investment', { amount: formatCurrency(totalMaintenanceCost) })}
            </span>
          </div>
        </div>
        <Button
          variant="primary"
          iconName="Plus"
          iconPosition="left"
          onClick={() => {
            setRecordToEdit(null);
            setShowAddModal(true);
          }}
          className="mt-4 sm:mt-0"
        >
          {t('asset_details.log_maintenance')}
        </Button>
      </div>

      <MaintenanceAdvisor asset_id={assetTag} />

      {/* Maintenance Timeline */}
      <div className="space-y-4">
        {!maintenanceHistory || maintenanceHistory.length === 0 ? (
          <div className="text-center py-12 bg-muted/20 rounded-xl border border-dashed border-border">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Wrench" size={32} className="text-muted-foreground" />
            </div>
            <h4 className="text-lg font-medium text-foreground mb-2">{t('asset_details.no_maintenance_records')}</h4>
            <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
              {t('asset_details.no_maintenance_description')}
            </p>
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={() => {
                setRecordToEdit(null);
                setShowAddModal(true);
              }}
            >
              {t('asset_details.add_first_record')}
            </Button>
          </div>
        ) : (
          maintenanceHistory?.map((record, index) => (
            <div key={record?.id} className="relative">
              {/* Timeline Line */}
              {index !== maintenanceHistory?.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-full bg-border" />
              )}
              
              {/* Maintenance Record Card */}
              <div className="flex space-x-4">
                {/* Timeline Dot */}
                <div className="flex-shrink-0 w-12 h-12 bg-card border-2 border-primary rounded-full flex items-center justify-center relative z-10 shadow-sm">
                  <Icon 
                    name={record?.type === 'Repair' ? 'AlertTriangle' : 'Wrench'} 
                    size={20} 
                    className="text-primary" 
                  />
                </div>
                
                {/* Record Content */}
                <div className="flex-1 bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-foreground mb-1">{record?.title || `${record?.type} - ${record?.vendor}`}</h4>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-foreground opacity-80">
                        <span className="flex items-center gap-1.5">
                          <Icon name="Calendar" size={14} />
                          {formatDate(record?.maintenance_date, preferredFormat)}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getMaintenanceTypeColor(record?.type)}`}>
                          {record?.type || 'Standard'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end mt-2 sm:mt-0">
                      <div className="text-lg font-bold text-foreground">
                        {formatCurrency(record?.cost)}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center justify-end gap-1.5 mb-2 font-sans">
                        <Icon name="User" size={14} />
                        {t('asset_details.by')} {record?.vendor || record?.performed_by || t('asset_details.unknown')}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          iconName="Edit3"
                          title="Edit"
                          onClick={() => {
                            setRecordToEdit(record);
                            setShowAddModal(true);
                          }}
                          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          iconName="Trash2"
                          title="Delete"
                          onClick={() => handleDelete(record.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-error hover:bg-error/10"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-foreground opacity-80 bg-muted/30 p-3 rounded border border-border/50">
                    {record?.description || t('asset_details.no_description')}
                  </p>

                  {record.asset_attachments && record.asset_attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/20">
                      <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5 font-sans">
                        <Icon name="Paperclip" size={14} className="text-muted-foreground/75" />
                        <span>Attached Document:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {record.asset_attachments.map((file) => (
                          <button
                            key={file.id}
                            type="button"
                            onClick={() => {
                              const { data } = supabase.storage
                                .from('asset_attachments')
                                .getPublicUrl(file.file_path);
                              if (data?.publicUrl) window.open(data.publicUrl, '_blank');
                            }}
                            className="inline-flex items-center gap-1.5 bg-muted/60 hover:bg-muted border border-border px-2.5 py-1.5 rounded-md text-xs text-foreground font-medium transition font-sans"
                          >
                            <Icon name={file.file_type?.startsWith('image/') ? 'Image' : 'FileText'} size={14} className="text-muted-foreground/80" />
                            <span className="truncate max-w-[200px]">{file.file_name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Additional info could go here, like parts replaced if we add that later */}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Maintenance Form Modal */}
      <AddMaintenanceModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setRecordToEdit(null);
        }}
        assetTag={assetTag}
        onSuccess={onSuccess}
        recordToEdit={recordToEdit}
      />
    </div>
  );
};

export default MaintenanceTab;
